import modal from "bootstrap/js/dist/modal";

const COUNTRY_TO_LANGUAGE_MAP = {
  at: "de",
  ch: "de",
  be: "nl",
  mx: "es",
  br: "pt",
  ca: "en-US",
  gb: "en-gb",
  us: "en-US",
  au: "en-US",
  ie: "en-US",
  nz: "en-US",
};

const UC_SERVICE_NAME = "Usercentrics";
const USER_LANG_PROMPT_SHOWN_STORAGE_KEY = "userLangPromptShown";
const USER_COUNTRY_KEY = "uc_user_country";
let popupActive = false;

function getCountryData() {
  const countryCode = getCountryCode();

  if (!countryCode) {
    return null;
  }

  const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
  const countryName = regionNames.of(countryCode);

  return {
    countryCode: countryCode,
    countryName: countryName,
  };
}

function getCountryCode() {
  let data = sessionStorage.getItem(USER_COUNTRY_KEY);
  if (!data) {
    return null;
  }

  try {
    let country = JSON.parse(data);
    return country.countryCode || null;
  } catch (e) {
    return null;
  }
}

// check if user has consented to use the Usercentrics service, which will basically always be true since it's an essential (required) service
function hasUserConsentedToUserCentrics() {
  if (typeof UC_UI === "undefined" || !UC_UI.getServicesBaseInfo) {
    return false;
  }

  const services = UC_UI.getServicesBaseInfo();
  const userCentricsService = services.find((service) =>
    service.name.includes(UC_SERVICE_NAME)
  );

  if (!userCentricsService?.consent?.history?.length || userCentricsService?.consent?.history?.length <= 1) {
    return false;
  }

  // Get the most recent consent action
  const history = userCentricsService.consent.history;
  const mostRecentAction = history[history.length - 1];

  // Check if the most recent action was to deny all services
  if (mostRecentAction.action === "onDenyAllServices") {
    return false;
  }

  // For other actions (like onInitialPageLoad), check if the status is true
  // This handles cases where the user hasn't made an explicit choice yet
  return userCentricsService?.consent?.status === true;
}

function getLanguageLinksData() {
  const langPrompt = document.getElementById("langPrompt");
  if (!langPrompt) {
    return null;
  }

  const languageLinksData = langPrompt.getAttribute("data-language-links");
  if (!languageLinksData) {
    return null;
  }

  try {
    return JSON.parse(languageLinksData);
  } catch (error) {
    return null;
  }
}

function updateModalContent(
  countryData,
  currentLang,
  userLanguageObj,
  isFallbackToEnglish = false
) {
  const langPrompt = document.getElementById("langPrompt");
  const modalBody = langPrompt.querySelector("[data-lang-prompt-message]");
  if (modalBody) {
    if (isFallbackToEnglish) {
      modalBody.innerHTML = `
        We see you are visiting us from <strong>${countryData.countryName}</strong>.
        This site is not available in your language.
        Would you like to go to the <strong>EN</strong> site instead?
      `;
    } else {
      modalBody.innerHTML = `
        We see you are visiting us from <strong>${
          countryData.countryName
        }</strong>.
        This is the <strong>${currentLang.toUpperCase()}</strong> version of the site.
        If you would like to visit the <strong>${userLanguageObj.hreflang.toUpperCase()}</strong> version,
        click below.
      `;
    }
  }

  const goToButton = langPrompt.querySelector("[data-lang-go-button]");
  if (goToButton) {
    goToButton.setAttribute("data-localUrl", userLanguageObj.url);
    if (isFallbackToEnglish) {
      goToButton.textContent = "Go to EN site";
    } else {
      goToButton.textContent = `Go to ${userLanguageObj.hreflang.toUpperCase()} site`;
    }
  }

  const stayButton = langPrompt.querySelector("[data-lang-stay-button]");
  if (stayButton) {
    stayButton.textContent = `Stay on ${currentLang.toUpperCase()} site`;
  }
}

function setupModalEventListeners(newModal) {
  newModal.addEventListener(
    "hide.bs.modal",
    () => {
      localStorage.setItem(USER_LANG_PROMPT_SHOWN_STORAGE_KEY, "true");
      const url = document.activeElement?.getAttribute("data-localUrl");
      if (url) {
        window.location.href = url;
      }
    },
    { once: true }
  );
}

function showLanguageModal() {
  const langPrompt = document.getElementById("langPrompt");
  if (!langPrompt) return;

  setupModalEventListeners(langPrompt);

  const bootstrapModal = new modal(langPrompt);
  bootstrapModal.show();
}

// Umbraco JSON uses hreflang "EN" for the global en-US culture only (see LanguageLinkExtensions).
function normalizeCurrentLangForCompare(currentLang) {
  if (!currentLang) return "";
  const c = String(currentLang).toLowerCase().replace(/_/g, "-");
  if (c === "en") return "en-us";
  return c;
}

function normalizePreferredLang(pref) {
  return (pref || "").toLowerCase().replace(/_/g, "-");
}

/**
 * True when the user is already on the site variant we would suggest.
 * Avoids broad startsWith("en") so en-GB visitors on the global EN site still get the UK prompt.
 */
function isSameLanguage(userPreferredLang, currentLang) {
  const u = normalizePreferredLang(userPreferredLang);
  const c = normalizeCurrentLangForCompare(currentLang);
  if (!u || !c) return false;
  if (u === c) return true;
  // Two-letter map value (e.g. de) matches de-DE, de-AT, etc.
  if (u.length === 2 && c.startsWith(`${u}-`)) return true;
  if (c.length === 2 && u.startsWith(`${c}-`)) return true;
  return false;
}

function languageMatchesPreferred(lang, userPreferredLang) {
  const pref = normalizePreferredLang(userPreferredLang);
  if (!pref) return false;
  const code = normalizePreferredLang(lang.code);
  const href = normalizePreferredLang(lang.hreflang);
  return code === pref || href === pref;
}

// Prefer global English, then any other English culture (stable order for fallback).
function findFallbackEnglishLanguage(languages) {
  const list = languages || [];
  return (
    list.find((lang) => normalizePreferredLang(lang.code) === "en-us") ||
    list.find((lang) => normalizePreferredLang(lang.hreflang) === "en") ||
    list.find((lang) => normalizePreferredLang(lang.code).startsWith("en-"))
  );
}

function processCountryData(countryData, languageLinks) {
  const userCountryCode = countryData.countryCode.toLowerCase();
  const currentLang = languageLinks.currentLang;

  // Use the mapping for known countries, otherwise use the country code as fallback
  const userPreferredLang =
    COUNTRY_TO_LANGUAGE_MAP[userCountryCode] || userCountryCode;

  if (!userPreferredLang || isSameLanguage(userPreferredLang, currentLang)) {
    return;
  }

  // First, try to find the user's preferred language (ISO codes are case-insensitive)
  let userLanguageObj = languageLinks.languages.find((lang) =>
    languageMatchesPreferred(lang, userPreferredLang)
  );

  // If preferred language is not available, try to find English as fallback
  if (!userLanguageObj) {
    userLanguageObj = findFallbackEnglishLanguage(languageLinks.languages);
  }

  if (userLanguageObj) {
    const isFallbackToEnglish = !languageLinks.languages.some((lang) =>
      languageMatchesPreferred(lang, userPreferredLang)
    );

    // Don't show popup if we're already on the English site (global EN only)
    if (isFallbackToEnglish && isSameLanguage("en-us", currentLang)) {
      return;
    }

    updateModalContent(
      countryData,
      currentLang,
      userLanguageObj,
      isFallbackToEnglish
    );
    showLanguageModal();
  }
}

function isLanguagePromptShown() {
  const langPromptShown = localStorage.getItem(
    USER_LANG_PROMPT_SHOWN_STORAGE_KEY
  );
  return langPromptShown && langPromptShown === "true";
}

async function doIpLookup() {
  if (popupActive || isLanguagePromptShown()) {
    return;
  }

  const languageLinks = getLanguageLinksData();
  if (!languageLinks) return;

  let countryData = getCountryData();

  if (!countryData) {
    return;
  }

  popupActive = true;

  processCountryData(countryData, languageLinks);
}

function initCookieConsent() {
  // Listen for changes in UserCentrics consent
  window.addEventListener("UC_UI_CMP_EVENT", (event) => {
    const eventType = event.detail.type;
    if (
      ["ACCEPT_ALL", "DENY_ALL", "SAVE"].includes(eventType) &&
      hasUserConsentedToUserCentrics()
    ) {
      doIpLookup();
    }
  });

  // wait for UserCentrics to be initialized, then check if user has consented
  window.addEventListener("UC_UI_INITIALIZED", function () {
    if (hasUserConsentedToUserCentrics()) {
      doIpLookup();
    }
  });
}

initCookieConsent();
